import { Decimal, Maybe } from '../../common/data.js';
import { h } from 'preact';
import { getContentUrlPrefix, makeDesignPreviewUrl, wrapImageUrl } from '../lib/images.js';
import { Icon } from './icons.js';
import { apply, ensure, formatPrice, isIn, isSome, logRejection, maybe, notEmpty } from '../../common/util.js';
import { HTMLAttributes, memo, TargetedEvent, useContext, useEffect, useMemo } from 'preact/compat';
import {
	CartData,
	CartTotals,
	expandCart,
	getCartName,
	isProductItem,
	SavedCartEntry,
} from '../../common/data/cart.js';
import { activateCart, logOut, relinquishAccountAccess, selectAccount } from '../lib/api.js';
import { ClassNameArray, classNames } from '../lib/util.js';
import { findActiveMenu, MenuEntries, MenuEntry } from '../../common/data/menus.js';
import { SiteContext } from '../context.js';
import { AccountAvatar } from './avatar.js';
import { IconName } from '../../common/data/icons.js';
import { AccountAccessData, permissionsInclude } from '../../common/data/accounts.js';
import { computed, Signal, useSignal, useSignalEffect } from '@preact/signals';
import { alertOnError, showErrorAlert } from './popup.js';
import { setActiveAccount } from '../lib/sw-glue.js';
import { formatTimestamp } from '../../common/data/dates.js';
import { useRef, useState } from 'preact/hooks';

const doLogOut = async (e?: Event) => {
	e?.preventDefault();
	await logOut();
	location.href = '/';
};

function isCartNonEmpty(cart: Maybe<CartData>): cart is CartData & { totals: CartTotals<Decimal> } {
	return !!(cart && cart.totals && Number(cart.totals.product));
}

export const checkIfDesktop = () => {
	if (typeof window != 'object') return null;
	return window.innerWidth > 872;
};

export interface MenuItemProps extends Omit<Partial<HTMLAttributes<HTMLLIElement>>, 'class' | 'className'> {
	classes: ClassNameArray;
}

function MenuItem({ classes, children, ...props }: MenuItemProps) {
	const [open, setOpen] = useState<Maybe<boolean>>(null);
	const close = () => {
		setOpen(false);
		setTimeout(() => setOpen(null), 20);
	};
	const ref = useRef<HTMLLIElement>(null);
	return <li
		ref={ref}
		class={classNames(...classes, open && '--open', open === false && '--closed')}
		onKeyDown={e => {
			if (e.ctrlKey || e.altKey || e.shiftKey) return;
			if (!open && isIn(e.key, ['Enter', ' '])) {
				e.preventDefault();
				e.stopPropagation();
				setOpen(true);
			}
			else if (open && e.key == 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				ref.current?.querySelector('a')?.focus();
				close();
			}
		}}
		onFocusOut={e => setTimeout(() => {
			if (!document.activeElement || maybe(ref.current, el => !el.contains(document.activeElement))) {
				close();
			}
		}, 100)}
		onTouchStart={e => {
			if (open) return;
			e.preventDefault();
			e.stopPropagation();
			ref.current?.querySelector('a')?.focus();
			setOpen(true);
		}}
	>{children}</li>;
}

export function Header() {
	
	const sc = useContext(SiteContext);
	const [mainMenu, doubleHeader] = [sc.mainMenu.value, sc.doubleHeader.value];
	
	const filter = (me: MenuEntry) => !me.tags?.includes('mobile-only') && !me.tags?.includes('skip-header');
	
	return <header class="header kbd-area">
		
		<HeaderLogo />
		
		<ul className="header-top-menu" role="navigation" aria-label="Main">
			{mainMenu.entries?.filter(filter).map((item) => apply(
				maybe(item.entries?.filter(filter), e => e.length ? e : null) ?? null,
				item.tags?.includes('mega'),
				(entries, mega) => <MenuItem
					classes={[
						'header-top-menu__item',
						mega && entries && '--mega',
						!mega && entries && '--submenu',
					]}
				>
					<a href={item.link} className="header-top-menu__link">
						{item.title}
						{entries && <svg width="24px" height="24px" aria-hidden="true" className="header-top-menu__icon" viewBox="0 0 96.154 96.154">
							<path
								d="M.561 20.971l45.951 57.605c.76.951 2.367.951 3.127 0l45.956-57.609c.547-.689.709-1.716.414-2.61a2.686 2.686 0 00-.186-.437 2.004 2.004 0 00-1.765-1.056H2.093c-.736 0-1.414.405-1.762 1.056a2.62 2.62 0 00-.184.426c-.297.905-.136 1.934.414 2.625z"
							></path>
						</svg>}
					</a>
					{mega && entries && <div className="header-top-menu__mega">
						{entries.map((dim) => <div className="header-top-menu__mega-column">
							<h5>{dim.title}</h5>
							{maybe(dim.entries, sub => <ul className="header-top-menu__mega-group">
								{sub.filter(filter).map(category => <li
									className="header-top-menu__mega-item"
								>
									<a href={category.link} className="header-top-menu__mega-link">{category.title}</a>
								</li>)}
							</ul>)}
						</div>)}
					</div>}
					{!mega && entries && <ul className="header-submenu header-submenu--under-text">
						{entries.map(me => <li className="header-submenu__item">
							<a href={me.link} className="header-submenu__link">{me.title}</a>
						</li>)}
					</ul>}
				</MenuItem>,
			))}
		</ul>
		
		<HeaderIcons withPhoneNumber={doubleHeader} />
	
	</header>;
	
}

function HeaderLogo() {
	const sc = useContext(SiteContext);
	const { toggleSidebar } = sc;
	const { host, brand_name, logo } = sc.domain.value;
	return <div className="header-logo">
		<button
			className="header-logo__menu"
			onClick={toggleSidebar}
			onKeyDown={toggleSidebar}
			title="Account Menu"
		/>
		<a href={`https://${host}`} className="header-logo__link" title="Homepage">
			<img
				className="header-logo__img"
				alt={brand_name}
				src={`${getContentUrlPrefix()}${logo}`}
				width="200"
			/>
		</a>
	</div>;
}

function HeaderIcons({ withPhoneNumber }: { withPhoneNumber: boolean }) {
	const sc = useContext(SiteContext);
	const { user, account, accountAccess, accounts, cart, domain, pathname, unreadMessageCounts, context } = sc;
	const accountSwitcher = (aa: AccountAccessData) => alertOnError(async (e: TargetedEvent<HTMLAnchorElement, MouseEvent>) => {
		
		if (e.shiftKey || e.ctrlKey || e.altKey || e.button) return;
		e.preventDefault();
		e.stopPropagation();
		
		const res = await selectAccount({ account: aa.to_account });
		
		await setActiveAccount(sc, ensure(res.user), ensure(res.account));
		
		/**
		 * TODO: could use DashboardPage.instance.navigate here, however...
		 * there is a lot of SiteData besides user/account that needs updating, so do noob mode whole page reload.
		 * ...for now
		 */
		const match = location.pathname.match(/^(\/account\/)(\w+)(.*?)$/);
		if (match && !isIn(match[2], ['store', 'user'])) location.href = [match[1], res.account!.id, match[3]].join('');
		else if (location.pathname.indexOf('/cart/') == 0) location.href = '/cart';
		else location.reload();
		
	});
	const cartSwitcher = (c: SavedCartEntry) => alertOnError(async (e: TargetedEvent<HTMLAnchorElement, MouseEvent>) => {
		
		if (e.shiftKey || e.ctrlKey || e.altKey || e.button) return;
		e.preventDefault();
		e.stopPropagation();
		
		const { cart } = await activateCart({ id: c.id });
		await sc.setActiveCart(expandCart(cart));
		
		if (location.pathname.indexOf('/cart/') == 0) location.href = `/cart/${cart.id}`;
		
	});
	return <ul className={classNames('header-icons', '--right', user.value ? '--user' : '--guest')}>
		{!user.value && <MenuItem classes={['header-icons__item']}>
			<a href="/faq" className="header-icons__link" title="FAQ">
				<div className="header-icons__link--icon">
					<Icon name="faq" aria-hidden="true"/>
				</div>
			</a>
		</MenuItem>}
		{user.value && <MenuItem classes={['header-icons__item', '--small-submenu']}>
			<a
				href={`/account/${account.value?.id ?? 'create'}`}
				className={classNames(
					'header-icons__link',
					'--avatar',
					accountAccess.value?.special && '--bounce'
				)}
				title={account.value?.account_name ?? 'Account'}
			>
				<AccountAvatar account={account.value} />
			</a>
			<ul className="header-submenu header-submenu--under-icon">
				
				{accounts.value?.filter(aa => permissionsInclude(aa.permissions, 'act_as_account')).slice(0, 5).map(aa => {
					if (account.value?.id != aa.to_account) return <li className="header-submenu__item">
						<a
							href={`/account/${aa.to_account}`}
							className="header-submenu__link"
							title={`Switch to: ${aa.account_name}`}
							onClick={accountSwitcher(aa)}
						>
							<AccountAvatar record={aa} />
							<span>{aa.account_name ?? aa.account_email ?? aa.to_account}</span>
							{maybe(unreadMessageCounts.value?.accounts[aa.to_account], n => !!n && <abbr
								class="header-submenu__unread"
								title={`${n} unread messages`}
							>{n}</abbr>)}
						</a>
					</li>;
					return <>
						<li className="header-submenu__item">
							<a
								href={`/account/${aa.to_account}`}
								className="header-submenu__link"
								title={`Active account: ${aa.account_name}`}
							>
								<AccountAvatar record={aa} />
								<span>{aa.account_name ?? aa.account_email ?? aa.to_account}</span>
							</a>
							<a
								href={`/account/${aa.to_account}/messages`}
								className="header-submenu__link"
							>
								<Icon name={'letter'} />
								<span>Messages</span>
								{maybe(unreadMessageCounts.value?.accounts[aa.to_account], n => !!n && <abbr
									className="header-submenu__unread"
									title={`${n} unread messages`}
								>{n}</abbr>)}
							</a>
							<a
								href={`/account/${aa.to_account}`}
								className="header-submenu__link"
							>
								<Icon name={'two-grid'} />
								<span>Dashboard</span>
							</a>
							<a
								href={`/account/${aa.to_account}/settings`}
								className="header-submenu__link"
							>
								<Icon name={'gear'} />
								<span>Settings</span>
							</a>
							{!!aa.special && maybe(aa.via_ids?.[0], id => <a
								className="header-submenu__link"
								onClick={async e => {
									e.stopPropagation();
									e.preventDefault();
									await relinquishAccountAccess({ id: aa.via_ids![0]! }).catch(showErrorAlert);
									location.reload();
								}}
							>
								<Icon name={'log-out'} />
								<span>Exit</span>
							</a>)}
						</li>
					</>;
				})}
				
				{accounts.value && accounts.value.length > 2 && <li className="header-submenu__item">
					<a href="/account/user/accounts" className="header-submenu__link">
						<Icon name={'team'} />
						<span>All accounts ({accounts.value.length})</span>
					</a>
				</li>}
				<li className="header-submenu__item">
					<a href="/account/create" className="header-submenu__link">
						<Icon name={'sign-up'} />
						<span>New account</span>
					</a>
				</li>
				<li className="header-submenu__item">
					<a href="/account/user/settings" className="header-submenu__link header-submenu__link--email">
						<Icon name={'user'} />
						<span>{user.value.email}</span>
					</a>
				</li>
				
				<li className="header-submenu__item">
					<a className="header-submenu__link header__log-out" href="#" onClick={doLogOut}>
						<Icon name="arrow-left" />
						<span>Sign Out</span>
					</a>
				</li>
			
			</ul>
		</MenuItem>}
		{user.value && <MenuItem classes={['header-icons__item']}>
			<a href="/account/messages" className="header-icons__link" title="Messages & Support">
				<div className="header-icons__link--icon">
					<Icon name="letter" aria-hidden="true"/>
					{maybe(unreadMessageCounts.value?.total, n => !!n && <abbr
						class="header-icons__unread"
						title={`${n} unread messages`}
					>{n}</abbr>)}
				</div>
			</a>
			<ul className="header-submenu header-submenu--under-icon">
				<li className="header-submenu__item">
					<a href="/account/messages" className="header-submenu__link">
						<Icon name="letter" />
						<span>All messages</span>
					</a>
				</li>
				<li className="header-submenu__item">
					<a href="/faq" className="header-submenu__link">
						<Icon name="faq" />
						<span>FAQ</span>
					</a>
				</li>
				<li className="header-submenu__item">
					<a href="/contact" className="header-submenu__link">
						<Icon name="email" />
						<span>Contact us</span>
					</a>
					{maybe(domain.value.phone_number, p => <a href={`tel:${p}`} className="header-submenu__link">
						<Icon name="phone-short" /><span>Call us now</span>
					</a>)}
				</li>
			</ul>
		</MenuItem>}
		{!user.value && <MenuItem classes={['header-icons__item']}>
			<a
				href={maybe(context.value.authHost, ah => `//${ah}/auth/begin?host=${domain.value.host}&url=${encodeURIComponent(pathname.value)}`) ?? `/auth?url=${encodeURIComponent(pathname.value)}`}
				className="header-icons__link"
				title="Account"
			>
				<Icon name="account" aria-hidden="true"/>
			</a>
		</MenuItem>}
		{user.value?.admin && <MenuItem classes={['header-icons__item']}>
			<a href="/admin" className="header-icons__link" title="Admin">
				<Icon name="folder" aria-hidden="true"/>
			</a>
		</MenuItem>}
		<MenuItem classes={['header-icons__item']}>
			<a href={cart.value ? `/cart/${cart.value.id}` : `/cart`} className="header-icons__link header-icons__link--cart" title="Cart">
				<div className="header-icons__link--icon">
					<Icon name="cart2" aria-hidden="true"/>
				</div>
				{isCartNonEmpty(cart.value) && <span className="header-icons__cart">{formatPrice(cart.value.totals.effectiveProduct)}</span>}
			</a>
			{maybe(cart.value, cart => <ul className="header-submenu header-submenu--rightmost">
				<li className="header-submenu__item">
					<a href={`/cart/${cart.id}`} className="header-submenu__link">
						<span>{getCartName(cart)}</span>
						<span>{`(${formatPrice(cart.totals.payable)}, ${formatTimestamp(cart.updated)})`}</span>
					</a>
					<a href={`/cart/${cart.id}`} class="header-submenu__cart-contents">
						{
							cart.items
								.filter(it => isProductItem(it))
								.filter(it => isSome(it.design) || isSome(it.preview))
								.map(it => <img src={
									maybe(it.design, did => makeDesignPreviewUrl(did, {
										format: 'webp',
										previewWidth: 96,
										side: 'front',
									}))
								?? wrapImageUrl(it.preview!)
								} alt={it.name ?? undefined} />)
						}
					</a>
				</li>
				{notEmpty(cart.savedCarts) && <li className="header-submenu__item">
					{cart.savedCarts.slice(0, 5).map(c => <a
						href={`/cart/${c.id}`}
						className="header-submenu__link"
						onClick={cartSwitcher(c)}
					>
						<Icon name={c.id == cart.id ? 'radio-active' : 'radio'} />
						<span>{getCartName(c)}</span>
						<span>{`(${formatPrice(c.total ?? 0)}, ${formatTimestamp(c.updated)})`}</span>
					</a>)}
					{maybe(account.value, a => <a
						href={`/account/${a.id}/saved-carts`}
						className="header-submenu__link"
					>
						<Icon name="cart" />
						Manage carts
					</a>)}
				</li>}
			</ul>)}
		</MenuItem>
		{maybe(domain.value.phone_number, p => <MenuItem classes={['header-icons__item', '--contact']}>
			<a
				href={`tel:${p}`}
				className="header-icons__link header-icons__contacts"
				title="Call us"
			>
				<Icon name="phone-short" aria-hidden="true"/>
				{withPhoneNumber && <div className="header-icons__contacts-right">
					<span className="header-icons__contacts-title">{domain.value.phone_text ?? 'Talk to a real person'}</span>
					<span className="header-icons__contacts-phone">{p}</span>
				</div>}
			</a>
		</MenuItem>)}
	</ul>;
}

export function AboveHeader() {
	const { domain } = useContext(SiteContext);
	return <header class="above-header kbd-area">
		<div class="above-header__left" />
		<div className="above-header__center">
			<span dangerouslySetInnerHTML={{ __html: domain.value.top_text ?? '' }} />
		</div>
		<HeaderIcons withPhoneNumber={false} />
	</header>;
}

export function Sidebar () {

	const active = useSignal<Maybe<MenuEntries>>(null);
	const self = useRef<HTMLElement>(null);
	
	const { mainMenu, cart, pathname, query, userMenus, auxMenu, sidebarOpen, closeSidebar, sidebarLocked } = useContext(SiteContext);
	
	const combinedMenu = computed(() => ({
		...(mainMenu.value),
		entries: [
			...(userMenus.value),
			...(mainMenu.value.entries || []),
			...(auxMenu.value.entries || []),
		],
	}));
	
	useSignalEffect(() => {
		active.value = findActiveMenu([combinedMenu.value], pathname.value, query.value);
	});
	
	useEffect(() => {
		self.current!.addEventListener('keyboardleave', e => {
			console.log(e);
			if (!sidebarLocked.peek()) closeSidebar();
		});
	}, []);
	
	const keyDown = useMemo(() => (e: TargetedEvent<HTMLLIElement, KeyboardEvent>) => {
		if (e.key == 'Escape' && !sidebarLocked.peek()) {
			closeSidebar();
			document.querySelector<HTMLElement>('.header-logo__menu')?.focus();
			e.preventDefault();
			e.stopPropagation();
		}
	}, []);

	return <aside className={classNames('sidebar', sidebarOpen.value && '--visible', 'kbd-area')} onKeyDown={keyDown} ref={self}>

		<div className="sidebar__header">
			<HeaderLogo />
			<button className="sidebar__close" onClick={closeSidebar} title="Close">
				<Icon name="cross" aria-hidden="true" />
			</button>
		</div>
		
		{userMenus.value.map(m => <Menu menu={m} active={active} />)}
		<Menu menu={mainMenu.value} active={active} mobileOnly={true} />
		<Menu menu={auxMenu.value} active={active} />
		
	</aside>;

}

const dashPathRegex = /^\/(account|dashboard)\b/;

const Menu = memo(({ menu, path = [], active, mobileOnly }: {
	menu: MenuEntry;
	path?: MenuEntries;
	active: Signal<Maybe<MenuEntries>>;
	mobileOnly?: boolean;
}) => {

	const { user, cart } = useContext(SiteContext);
	
	const filter = (me: MenuEntry) => (
		!me.tags?.includes('skip-sidebar')
		&& !me.tags?.includes('hidden')
		&& (!user.value || !me.tags?.includes('skip-sidebar-for-user'))
	);
	
	const clickHandler = (me: MenuEntry, path: MenuEntries, hasEntries = false) => (e: MouseEvent) => {
		if (!me.link || (hasEntries && !e.button && !e.ctrlKey && !e.shiftKey)) e.preventDefault();
		if (me.tags?.includes('log-out')) doLogOut().catch(logRejection);
		if (me.link && !hasEntries && location.pathname.match(dashPathRegex) && !me.link.match(dashPathRegex)) return;
		console.log(hasEntries);
		if (hasEntries) active.value = maybe(
			active.peek(),
			active => active.includes(me) ? active.slice(0, active.indexOf(me)) : null
		) ?? [...path, me];
	};
	
	const level = path.length;
	const hasIcons = menu.entries?.every(it => isSome(it.icon) || isSome(it.image));
	
	return <ul
		className={classNames(
			'sidebar__menu',
			`--level-${level}`,
			active.value?.includes(menu) && '--active',
			mobileOnly && '--mobile-only',
		)}
		style={{
			'--level': level,
			'--entries': menu.entries?.length || 0,
		}}
	>
		{!level && isSome(menu.title) && <li className="sidebar__entry">
			<div class="sidebar__heading">
				{menu.icon && (
					typeof menu.icon == 'string'
						? <Icon name={menu.icon as IconName} />
						: menu.icon
				)}
				{menu.title}
			</div>
		</li>}
		{menu.entries?.filter(filter).map(me => {
			const hasEntries = !!me.entries?.length;
			const isActive = active.value?.includes(me);
			return <li
				className={classNames(
					'sidebar__entry',
					isActive && '--active',
					me.tags?.includes('mobile-only') && '--mobile-only',
				)}
			>
				<div
					className={classNames(
						'sidebar__row',
						isActive && '--active',
						hasEntries ? '--has-entries' : '--no-entries',
					)}
				>
					<a
						onClick={clickHandler(me, path, hasEntries)}
						className="sidebar__link"
						href={me.link || '#'}
						target={me.tags?.includes('pop-out') ? '_blank' : undefined} rel="noreferrer"
					>
						{hasIcons && me.icon && (
							typeof me.icon == 'string'
								? <Icon name={me.icon as IconName} />
								: { ...me.icon, props: { ...me.icon.props, 'aria-hidden': true } }
						)}
						<span className="sidebar__link-text">{me.title}</span>
						{me.tags?.includes('cart') && isCartNonEmpty(cart.value)
							&& <span className="sidebar__link-aside">{formatPrice(cart.value.totals.effectiveProduct)}</span>}
					</a>
				</div>
				{hasEntries && <Menu menu={me} path={[...path, me]} active={active} />}
			</li>;
		})}
	</ul>;
	
});
